import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { TimesheetInspectionPanel } from "@/components/TimesheetInspectionPanel";
import { MaterialBillingAttention } from "@/components/MaterialBillingAttention";
// Monthly Job Billing Detail — large workspace modal opened from the Job
// Billing report's "Details" action. Read/aggregate layer over the existing
// records; every figure reconciles to the main Job Billing row. Tabs: Summary,
// Daily Work, Labour, Machines, Supplies, Billing.
import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  X, Download, Receipt, CheckCircle2, Loader2, Sparkles, Pencil, Save,
  Clock, BarChart3, Users, Truck, Package, Activity, ChevronDown, Search,
  User, Wrench, BookOpen, FileText, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { SupplierItems, SupplierMaterialSummary, OriginalInvoice } from "@/components/invoices/SupplierItems";
import api from "@/lib/api";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, Legend,
} from "recharts";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtHrs = (n) => `${Number(n || 0).toLocaleString("en-NZ", { maximumFractionDigits: 2 })} h`;
const CAT = { labour: "#FF5F15", machines: "#3B82F6", supplies: "#71717A" };

const STATUS = {
  not_invoiced: { label: "Not yet invoiced", dot: "bg-amber-500", text: "text-amber-600" },
  part_invoiced: { label: "Part invoiced", dot: "bg-sky-500", text: "text-sky-600" },
  invoiced: { label: "Invoiced", dot: "bg-emerald-500", text: "text-emerald-600" },
  no_cost: { label: "No cost recorded", dot: "bg-zinc-400", text: "text-zinc-500" },
};

const fmtDay = (d) => { try { return new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" }); } catch { return d; } };
const fmtShort = (d) => { try { return new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "short" }); } catch { return d; } };

const TABS = [
  { id: "summary", label: "Summary", icon: BarChart3 },
  { id: "daily", label: "Daily Work", icon: Clock },
  { id: "labour", label: "Labour", icon: Users },
  { id: "travel", label: "Travel", icon: Clock },
  { id: "machines", label: "Machines", icon: Truck },
  { id: "supplies", label: "Supplies", icon: Package },
  { id: "billing", label: "Billing", icon: Receipt },
];

const EmbeddedContainer = ({children}) => <>{children}</>;
const EmbeddedContent = ({children, hideClose, ...props}) => <div {...props}>{children}</div>;

export function JobBillingDetailWorkspace({ job, sel, onClose, onInvoiceNow, onMarkInvoiced, marking, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("summary");
  const [sourceId, setSourceId] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const load = () => setRevision(v => v + 1);
  useEffect(() => {
    if (!job?.job_id) return;
    let current = true;
    setTab("summary"); setData(null); setError(""); setLoading(true);
    api.get(`/reports/job-billing/${job.job_id}/monthly-detail`, { params: { year: sel.year, month: sel.month }, __flSkipReadCache:true })
      .then(r => { if (current) setData(r.data); })
      .catch(() => { if (current) setError("Could not load billing details."); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [job?.job_id, sel.year, sel.month, revision]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/reports/job-billing/${job.job_id}/monthly-detail/pdf`, {
        params: { year: sel.year, month: sel.month }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `job-billing-${job.job_number || job.job_id}-${sel.year}-${String(sel.month).padStart(2, "0")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Monthly job billing report downloaded");
    } catch { toast.error("PDF download failed"); }
    finally { setPdfBusy(false); }
  };

  const s = data?.summary || {};
  const b = data?.billing || {};
  const st = STATUS[b.status] || STATUS.no_cost;

  const Container = embedded ? EmbeddedContainer : Dialog;
  const Content = embedded ? EmbeddedContent : DialogContent;
  return (
    <Container open={!!job} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Content hideClose
        className={embedded ? "p-0 gap-0 rounded-xl border flex flex-col bg-background" : "p-0 gap-0 rounded-xl w-[calc(100vw-24px)] sm:w-[calc(100vw-48px)] max-w-[1500px] h-[calc(100vh-24px)] sm:h-[calc(100vh-48px)] max-h-[920px] flex flex-col bg-zinc-50 dark:bg-[#0b0f16] overflow-hidden"}
        data-testid="job-billing-detail-workspace">

        <div className="shrink-0 px-4"><MaterialBillingAttention jobId={job?.job_id} onReview={() => setTab("supplies")} /></div>
        {/* Header */}
        <div className="shrink-0 bg-white dark:bg-[#101620] border-b border-zinc-200 dark:border-white/10 px-4 sm:px-6 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {job?.job_number && <span className="font-mono text-[11px] font-bold bg-[#09090B] text-[#FF5F15] px-1.5 py-0.5 rounded" data-testid="jbd-jobno">{job.job_number}</span>}
              <h2 className="text-base sm:text-lg font-black tracking-tight text-zinc-900 dark:text-white truncate">{job?.title}</h2>
              <span className="text-sm font-normal text-zinc-400">— {data?.label || sel.label}</span>
            </div>
            {job?.client_name && <div className="text-xs text-zinc-500 mt-0.5 truncate">{job.client_name}</div>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={downloadPdf} disabled={pdfBusy || loading}
              className="h-9 rounded-lg uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15 hidden sm:inline-flex" data-testid="jbd-export-pdf">
              {pdfBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Export PDF
            </Button>
            <Button size="sm" onClick={() => onInvoiceNow?.(job)}
              className="h-9 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-[11px]" data-testid="jbd-invoice-now">
              <Receipt className="w-3.5 h-3.5 mr-1.5" /> Invoice Now
            </Button>
            <Button size="sm" variant="outline" onClick={() => onMarkInvoiced?.(job)} disabled={marking === job?.job_id}
              className="h-9 rounded-lg uppercase tracking-wider font-bold text-[11px] border-zinc-300 dark:border-white/15 hidden sm:inline-flex" data-testid="jbd-mark-invoiced">
              {marking === job?.job_id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />} Mark Invoiced
            </Button>
            {!embedded && <button onClick={onClose} aria-label="Close billing details" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-zinc-100 dark:hover:bg-white/10 text-zinc-500" data-testid="jbd-close"><X className="w-5 h-5" /></button>}
          </div>
        </div>

        {loading ? (
          <div className="flex-1 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#FF5F15]" /></div>
        ) : !data ? (
          <div className="flex-1 grid place-items-center text-sm text-zinc-500"><div role={error ? "alert" : "status"}>{error || "No data."}{error && <Button variant="outline" onClick={load}>Retry</Button>}</div></div>
        ) : (
          <div className={embedded ? "min-w-0" : "flex-1 overflow-y-auto"} data-testid="jbd-scroll">
            <div className="px-4 sm:px-6 pt-4 space-y-4">
              <section aria-label="What happened">
                <h3 className="text-sm font-semibold mb-2">What happened</h3>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5">
                  <Kpi label="Actual worked" value={fmtHrs(s.actual_worked)} help="Employee time for payroll/timekeeping; includes travel and plant-operation time." onClick={() => setTab("daily")} />
                  <Kpi label="Site labour" value={s.allocation_review_count ? "Needs review" : fmtHrs(s.site_labour_hours)} help="Worked time allocated to site labour, excluding travel and exclusive plant." onClick={() => setTab("labour")} />
                  <Kpi label="Travel" value={`${fmtHrs(data.travel?.totals?.recorded)} recorded`} help={`${fmtHrs(data.travel?.totals?.billable)} client-billable; open for the billing decision and sources.`} onClick={() => setTab("travel")} />
                  <Kpi label="Machines / plant" value={fmtHrs(s.machine_hours)} help="Recorded fleet and equipment usage on this job." onClick={() => setTab("machines")} />
                  <Kpi label="Materials / supplies" value={`${data.supplies?.length || 0} summary rows`} onClick={() => setTab("supplies")} />
                </div>
              </section>
              <section aria-label="Cost and invoicing">
                <h3 className="text-sm font-semibold mb-2">Our cost and invoicing</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                  <Kpi label="Our cost" value={fmtMoney(s.total_cost)} help="Internal recorded cost, not the client charge." onClick={() => setTab("billing")} />
                  <Kpi label="Already invoiced" value={fmtMoney(b.invoiced)} onClick={() => setTab("billing")} />
                  <Kpi label="Billing status" value={st.label} onClick={() => setTab("billing")} />
                  <Kpi label="Client charge" value="Review invoice sources" help="Uses sell rates and source eligibility, not internal costs." onClick={() => setTab("billing")} />
                </div>
              </section>
              <BillingReadiness jobId={job?.job_id} year={sel.year} month={sel.month} allocationReview={s.allocation_review_count || 0} travelReview={data.travel?.totals?.review_count || 0} onReview={() => setTab("billing")} onMaterials={() => setTab("supplies")} />
            </div>

            {/* Tabs */}
            <div className="px-4 sm:px-6 mt-4 border-b border-zinc-200 dark:border-white/10 sticky top-0 bg-zinc-50/95 dark:bg-[#0b0f16]/95 backdrop-blur z-10">
              <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
                {TABS.map((t) => {
                  const Icon = t.icon; const active = tab === t.id;
                  return (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`jbd-tab-${t.id}`}
                      className={`shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${active ? "border-[#FF5F15] text-[#FF5F15]" : "border-transparent text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"}`}>
                      <Icon className="w-4 h-4" /> {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4">
              {tab === "summary" && <SummaryTab data={data} onReloadSummary={load} />}
              {tab === "daily" && <DailyWorkTab days={data.daily_work} onSource={setSourceId} />}
              {tab === "labour" && <LabourAllocationTab workers={data.labour} onSource={setSourceId} />}
              {tab === "travel" && <TravelTab travel={data.travel} />}
              {tab === "machines" && <MachinesTab machines={data.machines} job={data.job} period={data.label} />}
              {tab === "supplies" && <><MaterialBillingAttention jobId={job?.job_id} details /><SuppliesTab supplies={data.supplies} summary={data.supplier_material_summary} /></>}
              {tab === "billing" && <BillingTab data={data} onCategory={setTab} onInvoiceNow={() => onInvoiceNow?.(job)} onMarkInvoiced={() => onMarkInvoiced?.(job)} marking={marking} jobId={job?.job_id} />}
            </div>
          </div>
        )}
      <TimesheetSourceSheet sourceId={sourceId} onClose={() => setSourceId(null)} />
      </Content>
    </Container>
  );
}

function BillingReadiness({ jobId, year, month, allocationReview, travelReview, onReview, onMaterials }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let active = true;
    setState({ loading: true });
    const from_date = `${year}-${String(month).padStart(2, "0")}-01`;
    const to_date = `${year}-${String(month).padStart(2, "0")}-${new Date(year, month, 0).getDate()}`;
    Promise.all([
      api.get(`/jobs/${jobId}/charge-up/unbilled`, { params: { from_date, to_date } }),
      api.get(`/jobs/${jobId}/material-attention`, { __flSkipReadCache: true }),
    ]).then(([sources, materials]) => { if (active) setState({ sources: sources.data, materials: materials.data }); })
      .catch(() => { if (active) setState({ error: true }); });
    return () => { active = false; };
  }, [jobId, year, month]);
  const entries = state.sources?.entries || [];
  const missing = entries.filter(row => row.billing_state === "unbilled" && row.missing_rate).length;
  const documents = state.sources?.needs_review?.length || 0;
  return <section className="rounded-xl border bg-card p-4" aria-label="Billing readiness">
    <h3 className="font-semibold text-sm">Billing readiness</h3>
    {state.loading ? <p className="text-sm mt-2">Checking invoice sources…</p> : state.error ? <p className="text-sm mt-2">Readiness unavailable — review invoice sources before invoicing.</p> : <>
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 text-sm">
        <button className="underline" onClick={onReview}>Client charge: {missing || documents || allocationReview || travelReview || state.materials.billing_count ? "Needs review" : fmtMoney(state.sources.total)} · unbilled sources this period</button>
        <button className="underline" onClick={onMaterials}>Materials: {state.materials.billing_count ? `${state.materials.billing_count} records need review (whole job)` : "No material billing attention"}</button>
        <button className="underline" onClick={onReview}>{missing} source(s) need rates · {documents} supplier document(s) need review · {allocationReview} allocation(s) and {travelReview} travel record(s) need review</button>
      </div>
      <details className="mt-3 text-sm"><summary className="cursor-pointer font-medium">See client-charge sources</summary>
        <p className="text-xs text-muted-foreground my-2">Travel is included in labour. Amounts use existing source rates; invoice selections and adjustments still apply.</p>
        {entries.map((row, index) => <div key={`${row.lock_key}-${index}`} className="border-t py-2 flex flex-wrap justify-between gap-2"><span>{row.date} · {row.name} · {row.source_type} · {row.qty} {row.unit}</span><span>{row.missing_rate ? "Needs rate" : fmtMoney(row.amount)} · {row.billing_state.replaceAll("_", " ")}</span></div>)}
        {!entries.length && <p>No invoice sources in this period.</p>}
        <Button variant="outline" className="mt-3" onClick={onReview}>Review billing and invoice</Button>
      </details>
    </>}
  </section>;
}

function TimesheetSourceSheet({ sourceId, onClose }) {
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setRecord(null); setError(false);
    if (sourceId) api.get(`/timesheet-records/${encodeURIComponent(sourceId)}`)
      .then(({ data }) => { if (active) setRecord(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [sourceId]);
  return <Sheet open={!!sourceId} onOpenChange={open => { if (!open) onClose(); }}><SheetContent side="right" className="sm:max-w-2xl">
    <SheetHeader><SheetTitle>Timesheet source</SheetTitle><SheetDescription>{record?.user_name || "Source record"} · {record?.date || ""}</SheetDescription></SheetHeader>
    <div className="mt-4">{error ? <p role="alert">Could not load the source. Close and reopen to retry.</p> : record ? <TimesheetInspectionPanel ts={record} onBillingChange={onClose} /> : <p role="status">Loading timesheet…</p>}</div>
  </SheetContent></Sheet>;
}

function LabourAllocationTab({ workers, onSource }) {
  return <div className="space-y-3">
    <p className="text-sm text-muted-foreground">Site labour excludes travel and exclusive plant. Worker cost includes all paid work categories.</p>
    {(workers || []).map((worker, index) => <details key={worker.user_id || index} className="border rounded-xl bg-card p-4">
      <summary className="cursor-pointer flex flex-wrap justify-between gap-2"><span className="font-semibold">{worker.name} · {worker.role || "—"}</span><span>{worker.days} days · {worker.allocation_review_count ? "Allocation needs review" : `${fmtHrs(worker.site_hours)} site labour`} · {fmtMoney(worker.cost)} worker cost</span></summary>
      {[...(worker.usage || [])].sort((a,b) => (a.date || "").localeCompare(b.date || "")).map((row, i) => <div key={row.timesheet_id || i} className="border-t mt-3 pt-3 text-sm">
        <div className="flex flex-wrap justify-between gap-2"><strong>{fmtShort(row.date)}</strong><span>{row.allocation_state.replaceAll("_", " ")} · {row.invoice_id ? "Labour source linked to invoice" : "No labour invoice link"}</span></div>
        <dl className="grid grid-cols-2 sm:grid-cols-5 gap-3 my-3"><div><dt>Actual worked</dt><dd>{fmtHrs(row.actual_worked)}</dd></div>{row.allocation ? <><div><dt>Site labour</dt><dd>{fmtHrs(row.allocation.labour_hours)}</dd></div><div><dt>Travel</dt><dd>{fmtHrs(row.allocation.travel_hours)}</dd></div><div><dt>Plant</dt><dd>{fmtHrs(row.allocation.plant_hours)}</dd></div><div><dt>Total allocated</dt><dd>{fmtHrs(row.allocated_total)}</dd></div></> : <div>Allocation requires review</div>}</dl>
        {row.timesheet_id && <Button variant="link" className="px-0" onClick={() => onSource(row.timesheet_id)}>View timesheet →</Button>}
      </div>)}
    </details>)}
    {!workers?.length && <Empty>No worker records for this period.</Empty>}
  </div>;
}

const Kpi = ({ label, value, accent, onClick, help }) => {
  const Tag = onClick ? "button" : "div";
  return <Tag onClick={onClick} title={help} className={`text-left rounded-xl border p-3 ${onClick ? "hover:border-[#FF5F15] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF5F15]" : ""} ${accent ? "border-[#FF5F15]/50 bg-orange-50 dark:bg-[#FF5F15]/10" : "border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620]"}`} data-testid={`jbd-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[11px] font-bold uppercase tracking-wide text-zinc-500">{label}{help && " ⓘ"}</div>
    <div className="text-lg font-extrabold tabular-nums mt-1">{value}</div>
  </Tag>;
};

const Card = ({ title, icon: Icon, right, children, className = "", testid }) => (
  <div className={`rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] p-4 ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2 font-black text-zinc-900 dark:text-white">
        {Icon && <Icon className="w-4 h-4 text-[#FF5F15]" />} <span className="text-sm">{title}</span>
      </div>
      {right}
    </div>
    {children}
  </div>
);

// ---------------- SUMMARY ----------------
function SummaryTab({ data, onReloadSummary }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2"><MonthlyWorkSummary data={data} onSaved={onReloadSummary} /></div>
      <KeyMetrics s={data.summary} />
      <div className="lg:col-span-1"><CostBreakdown s={data.summary} /></div>
      <div className="lg:col-span-2"><DailyTotals series={data.daily_series} /></div>
      <TopLabour rows={data.labour} />
      <TopMachines rows={data.machines} />
      <TopSupplies rows={data.supplies} />
      <div className="lg:col-span-3"><RecentActivity items={data.activity} /></div>
    </div>
  );
}

function MonthlyWorkSummary({ data, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(data.monthly_summary || "");
  const [gen, setGen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setText(data.monthly_summary || ""); setEditing(false); }, [data.monthly_summary, data.ym]);

  const generate = async () => {
    setGen(true);
    try {
      const { data: r } = await api.post(`/reports/job-billing/${data.job.id}/monthly-summary/generate`, { year: data.year, month: data.month });
      setText(r.draft); setEditing(true);
      toast.success(`Draft written from ${r.note_count} daily note(s) — review & save`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate summary"); }
    finally { setGen(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/reports/job-billing/${data.job.id}/monthly-summary`, { year: data.year, month: data.month, content: text });
      toast.success("Monthly summary saved"); setEditing(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Card title="Monthly Work Summary" icon={FileText} testid="jbd-summary-card"
      right={<Button size="sm" variant="outline" onClick={generate} disabled={gen}
        className="h-8 rounded-lg text-[11px] font-bold border-[#FF5F15]/40 text-[#FF5F15] hover:bg-orange-50" data-testid="jbd-generate-summary">
        {gen ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Generate from daily notes
      </Button>}>
      {editing ? (
        <>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6}
            className="rounded-lg border-zinc-300 text-sm" placeholder="Describe the work performed this month…" data-testid="jbd-summary-textarea" />
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={save} disabled={saving} className="h-8 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold" data-testid="jbd-save-summary">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />} Save Summary
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setText(data.monthly_summary || ""); setEditing(false); }} className="h-8 rounded-lg text-[11px] font-bold">Cancel</Button>
          </div>
        </>
      ) : text ? (
        <>
          <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed whitespace-pre-wrap" data-testid="jbd-summary-text">{text}</p>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-8 rounded-lg text-[11px] font-bold mt-3" data-testid="jbd-edit-summary"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Summary</Button>
        </>
      ) : (
        <div className="text-sm text-zinc-400 italic py-2">No summary yet. Generate a draft from this month's daily notes, or write your own.</div>
      )}
    </Card>
  );
}

function KeyMetrics({ s }) {
  const rows = [
    ["Total days on site", s.days_on_site ?? 0],
    ["Site labour hours", fmtHrs(s.site_labour_hours)],
    ["Machine hours", fmtHrs(s.machine_hours)],
    ["Total cost", fmtMoney(s.total_cost)],
    ["Cost per day", fmtMoney(s.cost_per_day)],
    ["Average labour crew size", s.avg_crew_size ?? 0],
    ["Average machine units per day", s.avg_machines_per_day ?? 0],
  ];
  return (
    <Card title="Key Metrics" icon={BarChart3} testid="jbd-key-metrics">
      <div className="divide-y divide-zinc-100 dark:divide-white/5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between py-2 text-sm">
            <span className="text-zinc-600 dark:text-zinc-400">{k}</span>
            <span className="font-bold tabular-nums">{v}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CostBreakdown({ s }) {
  const parts = [
    { key: "Labour", value: s.labour_cost || 0, color: CAT.labour },
    { key: "Machines", value: s.machine_cost || 0, color: CAT.machines },
    { key: "Supplies", value: s.supplies_cost || 0, color: CAT.supplies },
  ];
  const total = s.total_cost || 0;
  const pie = parts.filter((p) => p.value > 0);
  return (
    <Card title="Cost Breakdown" icon={Clock} testid="jbd-cost-breakdown">
      <div className="flex items-center gap-4">
        <div className="relative w-[150px] h-[150px] shrink-0">
          {total > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="key" innerRadius={48} outerRadius={72} paddingAngle={2} stroke="none">
                  {pie.map((p) => <Cell key={p.key} fill={p.color} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="w-full h-full grid place-items-center text-xs text-zinc-400">No cost</div>}
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-center"><div className="text-sm font-extrabold tabular-nums">{fmtMoney(total)}</div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Total Cost</div></div>
          </div>
        </div>
        <div className="flex-1 space-y-2">
          {parts.map((p) => (
            <div key={p.key} className="flex items-center gap-2 text-sm">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
              <span className="flex-1 text-zinc-600 dark:text-zinc-400">{p.key}</span>
              <span className="font-bold tabular-nums">{fmtMoney(p.value)}</span>
              <span className="text-zinc-400 tabular-nums w-10 text-right">{total > 0 ? Math.round((p.value / total) * 100) : 0}%</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function DailyTotals({ series }) {
  const chart = (series || []).map((d) => ({ date: fmtShort(d.date), Labour: d.labour_hours, Machines: d.machine_hours }));
  return (
    <Card title="Daily Totals" icon={BarChart3} testid="jbd-daily-totals">
      {chart.length === 0 ? (
        <div className="h-[210px] grid place-items-center text-sm text-zinc-400">No work logged this month.</div>
      ) : (
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={chart} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#a1a1aa" }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: "#e4e4e7" }} />
            <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} tickLine={false} axisLine={false} />
            <Tooltip formatter={(v) => fmtHrs(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Labour" name="Labour / travel" stackId="a" fill={CAT.labour} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Machines" stackId="a" fill={CAT.machines} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function MiniTable({ title, icon, sub, head, rows, total, testid }) {
  return (
    <Card title={title} icon={icon} testid={testid}
      right={sub ? <span className="text-[10px] text-zinc-400 normal-case font-normal">{sub}</span> : null}>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
          {head.map((h, i) => <th key={i} className={`py-1.5 ${i === 0 ? "" : "text-right"}`}>{h}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} className="py-3 text-center text-zinc-400 text-xs italic">None</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="border-b border-zinc-50 dark:border-white/5 last:border-0">
              {r.map((c, j) => <td key={j} className={`py-1.5 ${j === 0 ? "font-medium truncate max-w-[180px]" : "text-right tabular-nums font-mono"}`}>{c}</td>)}
            </tr>
          ))}
          {total && rows.length > 0 && (
            <tr className="font-bold border-t-2 border-zinc-200 dark:border-white/10">
              {total.map((c, j) => <td key={j} className={`py-1.5 ${j === 0 ? "" : "text-right tabular-nums font-mono"}`}>{c}</td>)}
            </tr>
          )}
        </tbody>
      </table>
    </Card>
  );
}

const TopLabour = ({ rows }) => (
  <MiniTable title="Top Labour" icon={Users} sub="by hours" head={["Worker", "Days", "Hours", "Cost"]}
    testid="jbd-top-labour"
    rows={(rows || []).slice(0, 6).map((w) => [w.name, w.days, w.hours, fmtMoney(w.cost)])}
    total={["Total", (rows || []).reduce((a, w) => a + (w.days || 0), 0), Number((rows || []).reduce((a, w) => a + (w.hours || 0), 0).toFixed(2)), fmtMoney((rows || []).reduce((a, w) => a + (w.cost || 0), 0))]} />
);
const TopMachines = ({ rows }) => (
  <MiniTable title="Top Machines" icon={Truck} sub="by hours" head={["Asset", "Hours", "Cost"]}
    testid="jbd-top-machines"
    rows={(rows || []).slice(0, 6).map((m) => [m.label, m.hours, fmtMoney(m.cost)])}
    total={["Total", Number((rows || []).reduce((a, m) => a + (m.hours || 0), 0).toFixed(2)), fmtMoney((rows || []).reduce((a, m) => a + (m.cost || 0), 0))]} />
);
const TopSupplies = ({ rows }) => (
  <MiniTable title="Top Supplies" icon={Package} sub="by cost" head={["Item", "Qty", "Cost"]}
    testid="jbd-top-supplies"
    rows={(rows || []).slice(0, 6).map((sp) => [sp.item, sp.qty != null ? `${sp.qty} ${sp.unit || ""}`.trim() : "—", fmtMoney(sp.cost)])}
    total={["Total", "", fmtMoney((rows || []).reduce((a, sp) => a + (sp.cost || 0), 0))]} />
);

function RecentActivity({ items }) {
  return (
    <Card title="Recent Activity" icon={Activity} testid="jbd-recent-activity">
      {(!items || items.length === 0) ? (
        <div className="text-sm text-zinc-400 italic py-1">No recorded activity for this month.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
          {items.map((a, i) => (
            <div key={i} className="flex items-center gap-2.5 text-sm">
              <span className="w-2 h-2 rounded-full bg-[#FF5F15] shrink-0" />
              <span className="flex-1 text-zinc-700 dark:text-zinc-300 truncate">{a.event}</span>
              {a.ts && <span className="text-[11px] text-zinc-400 shrink-0">{fmtShort(a.ts)}</span>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------- DAILY WORK ----------------
function DailyWorkTab({ days, onSource }) {
  const [open, setOpen] = useState(() => new Set());
  const [q, setQ] = useState("");
  const toggle = (date) => setOpen((p) => { const n = new Set(p); n.has(date) ? n.delete(date) : n.add(date); return n; });
  const filtered = useMemo(() => {
    if (!q.trim()) return days || [];
    const t = q.toLowerCase();
    return (days || []).filter((d) => JSON.stringify(d).toLowerCase().includes(t));
  }, [days, q]);
  if (!days || days.length === 0) return <Empty>No hours logged on this job this month.</Empty>;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search daily notes…"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620] text-sm outline-none focus:border-[#FF5F15]" data-testid="jbd-daily-search" />
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(new Set(filtered.map((d) => d.date)))} className="h-9 rounded-lg text-[11px] font-bold" data-testid="jbd-expand-all">Expand all</Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(new Set())} className="h-9 rounded-lg text-[11px] font-bold" data-testid="jbd-collapse-all">Collapse all</Button>
      </div>
      <div className="space-y-2.5">
        {filtered.map((d) => {
          const isOpen = open.has(d.date);
          return (
            <div key={d.date} className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] overflow-hidden" data-testid={`jbd-day-${d.date}`}>
              <button onClick={() => toggle(d.date)} className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-zinc-50 dark:hover:bg-white/5">
                <div className="flex items-center gap-2 font-bold text-sm"><ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />{fmtDay(d.date)}</div>
                <div className="flex items-center gap-2 text-[11px] font-mono font-bold">
                  <span className="px-2 py-0.5 rounded bg-zinc-100 dark:bg-white/10">{d.labour_hours ?? d.worker_hours} h labour/travel</span>
                  {d.travel_hours > 0 && <span className="px-2 py-0.5 rounded bg-zinc-100 dark:bg-white/10">{fmtHrs(d.travel_hours)} recorded travel</span>}
                  {d.machine_hours > 0 && <span className="px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">{d.machine_hours} h machines</span>}
                </div>
              </button>
              {isOpen && <div className="px-4 py-2 space-y-2">{d.workers.map((worker, index) => <div key={worker.timesheet_id || index} className="text-sm"><span>{worker.name} · Actual worked {fmtHrs(worker.hours)}</span>{worker.allocation_summary?.allocation && <span> · Site {fmtHrs(worker.allocation_summary.allocation.labour_hours)} · Travel {fmtHrs(worker.allocation_summary.allocation.travel_hours)} · Plant {fmtHrs(worker.allocation_summary.allocation.plant_hours)}</span>}{worker.timesheet_id && <Button variant="link" onClick={() => onSource(worker.timesheet_id)}>View timesheet</Button>}</div>)}</div>}
              {isOpen && (
                <div className="px-4 pb-4 grid sm:grid-cols-2 gap-4 border-t border-zinc-100 dark:border-white/5 pt-3">
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2"><User className="w-3 h-3" /> Crew / daily notes</div>
                    <div className="space-y-2">
                      {d.workers.map((w, i) => (
                        <div key={i} className="text-xs">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0"><span className="font-bold">{w.name}</span>{w.start && w.end && <span className="text-zinc-500 ml-1.5 font-mono text-[10px]">{w.start}–{w.end}</span>}{w.status === "pending" && <span className="ml-1.5 text-[9px] font-bold uppercase text-amber-600">pending</span>}</div>
                            <div className="font-mono tabular-nums shrink-0 text-right leading-tight">
                              {(w.machine_hours || 0) > 0 ? (<><div className="font-bold">{w.labour_hours ?? w.hours} <span className="text-[9px] font-sans font-normal text-zinc-400">h lab</span></div><div className="text-orange-600 text-[11px]">{w.machine_hours} <span className="text-[9px] font-sans">h mach</span></div></>) : <span className="font-bold">{w.labour_hours ?? w.hours} h</span>}
                            </div>
                          </div>
                          {w.note && <div className="text-zinc-500 text-[11px] mt-0.5 whitespace-pre-wrap">{w.note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2"><Wrench className="w-3 h-3" /> Machines &amp; gear</div>
                    {(!d.machines || d.machines.length === 0) ? <div className="text-xs text-zinc-400 italic">None used</div> : (
                      <div className="space-y-1">{d.machines.map((m) => <div key={m.label} className="text-xs flex items-center justify-between gap-2"><span className="truncate">{m.label}</span><span className="font-mono font-bold tabular-nums shrink-0">{m.hours} h</span></div>)}</div>
                    )}
                    {d.summary && (<div className="mt-3 pt-3 border-t border-zinc-100 dark:border-white/5"><div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1"><BookOpen className="w-3 h-3" /> Day summary</div><p className="text-xs text-zinc-600 dark:text-zinc-300 whitespace-pre-wrap">{d.summary}</p></div>)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------- LABOUR ----------------
function workerDayRows(dailyWork, name) {
  const out = [];
  for (const d of dailyWork || []) {
    for (const w of d.workers || []) {
      if (w.name === name) out.push({ date: d.date, start: w.start, end: w.end, hours: w.hours, labour: w.labour_hours, machine: w.machine_hours, note: w.note });
    }
  }
  return out;
}
function LabourTab({ labour, dailyWork }) {
  const [open, setOpen] = useState(null);
  if (!labour || labour.length === 0) return <Empty>No labour recorded this month.</Empty>;
  return (
    <div className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] overflow-x-auto" data-testid="jbd-labour-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
          <th className="py-2.5 px-4">Worker</th><th className="px-3">Role</th><th className="px-3 text-right">Days</th><th className="px-3 text-right">Hours</th><th className="px-3 text-right">Cost</th><th className="px-3" />
        </tr></thead>
        <tbody>
          {labour.map((w) => {
            const isOpen = open === w.name;
            const detail = isOpen ? workerDayRows(dailyWork, w.name) : [];
            return (
              <Fragment key={w.name}>
                <tr className="border-b border-zinc-100 dark:border-white/5 hover:bg-zinc-50 dark:hover:bg-white/5 cursor-pointer" onClick={() => setOpen(isOpen ? null : w.name)} data-testid={`jbd-labour-row-${w.name}`}>
                  <td className="py-2.5 px-4 font-bold">{w.name}</td>
                  <td className="px-3 capitalize text-zinc-500">{w.role || "—"}</td>
                  <td className="px-3 text-right tabular-nums">{w.days}</td>
                  <td className="px-3 text-right tabular-nums font-mono">{w.hours}</td>
                  <td className="px-3 text-right tabular-nums font-mono font-bold">{fmtMoney(w.cost)}</td>
                  <td className="px-3 text-right"><ChevronDown className={`w-4 h-4 text-zinc-400 inline transition-transform ${isOpen ? "" : "-rotate-90"}`} /></td>
                </tr>
                {isOpen && detail.map((r, i) => (
                  <tr key={w.name + i} className="bg-zinc-50/60 dark:bg-white/5 text-xs border-b border-zinc-100 dark:border-white/5">
                    <td className="py-1.5 px-4 pl-8 font-mono text-zinc-500">{fmtShort(r.date)}</td>
                    <td className="px-3 text-zinc-500">{r.start && r.end ? `${r.start}–${r.end}` : "—"}</td>
                    <td className="px-3" />
                    <td className="px-3 text-right font-mono">{r.labour ?? r.hours} h</td>
                    <td className="px-3" colSpan={2}><span className="text-zinc-500">{r.note || ""}</span></td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
          <tr className="font-bold border-t-2 border-zinc-200 dark:border-white/10">
            <td className="py-2.5 px-4">Total</td><td className="px-3" />
            <td className="px-3 text-right tabular-nums">{labour.reduce((a, w) => a + (w.days || 0), 0)}</td>
            <td className="px-3 text-right tabular-nums font-mono">{Number(labour.reduce((a, w) => a + (w.hours || 0), 0).toFixed(2))}</td>
            <td className="px-3 text-right tabular-nums font-mono">{fmtMoney(labour.reduce((a, w) => a + (w.cost || 0), 0))}</td><td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function TravelTab({ travel }) {
  const [sourceId, setSourceId] = useState(null);
  const [source, setSource] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setSource(null); setError("");
    if (sourceId) api.get(`/timesheet-records/${encodeURIComponent(sourceId)}`)
      .then(({ data }) => { if (active) setSource(data); })
      .catch(() => { if (active) setError("Could not load this timesheet. Close and reopen to retry."); });
    return () => { active = false; };
  }, [sourceId]);
  const total = travel?.totals || {};
  const quantity = value => value == null ? "Review required" : fmtHrs(value);
  return <div className="space-y-4">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi label="Recorded travel" value={fmtHrs(total.recorded)} />
      <Kpi label="Client-billable travel" value={fmtHrs(total.billable)} />
      <Kpi label="Internal travel" value={fmtHrs(total.internal)} />
      <Kpi label="Available to Invoice Now" value={fmtHrs(total.invoice_available)} />
    </div>
    <p className="text-sm">{travel?.setting == null ? "Legacy billing: each timesheet’s travel decision applies." : travel.setting ? "Travel billing enabled." : "Travel billing disabled."}</p>
    <p className="text-xs text-zinc-500">Billable travel is included in invoice labour quantities, not charged again separately. Available hours exclude already invoiced and ineligible sources in this period; invoice selection and rates still apply.</p>
    {total.review_count > 0 && <p role="status">{total.review_count} record(s) require review and are excluded from these travel totals.</p>}
    {!travel?.rows?.length && <Empty>No recorded travel in this billing period.</Empty>}
    {[...(travel?.rows || [])].sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((row, index) => <div key={row.timesheet_id || index} className="border rounded-lg p-4 space-y-2">
      <div className="flex flex-wrap justify-between gap-2"><strong>{fmtShort(row.date)} · {row.worker || "—"}</strong><span className="text-xs uppercase text-zinc-500">{row.state.replaceAll("_", " ")}</span></div>
      <p className="text-xs">{row.source} · {row.billable == null ? "Review required" : row.billable > 0 ? "Billable" : "Internal"}{row.invoice_id ? " · Already invoiced" : ""}</p>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div><dt>Recorded</dt><dd>{quantity(row.recorded)}</dd></div><div><dt>Billable</dt><dd>{quantity(row.billable)}</dd></div>
        <div><dt>Internal</dt><dd>{quantity(row.internal)}</dd></div><div><dt>Available to invoice</dt><dd>{quantity(row.invoice_available)}</dd></div>
      </dl>
      {row.timesheet_id && <Button variant="link" className="px-0" onClick={() => setSourceId(row.timesheet_id)}>View timesheet →</Button>}
    </div>)}
    <Sheet open={!!sourceId} onOpenChange={open => { if (!open) setSourceId(null); }}>
      <SheetContent side="right" className="sm:max-w-2xl">
        <SheetHeader><SheetTitle>Travel source · Timesheet</SheetTitle><SheetDescription>{source?.user_name || "Source record"} · {source?.date || ""}</SheetDescription></SheetHeader>
        <div className="mt-5">{error ? <p role="alert">{error}</p> : source ? <TimesheetInspectionPanel ts={source} onBillingChange={() => setSourceId(null)} /> : <p role="status">Loading timesheet…</p>}</div>
      </SheetContent>
    </Sheet>
  </div>;
}

// ---------------- MACHINES ----------------
function MachinesTab({ machines, job, period }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  if (!machines || machines.length === 0) return <Empty>No machines or gear used this month.</Empty>;
  return (
    <>
    <div className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] overflow-x-auto" data-testid="jbd-machines-table">
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
          <th className="py-2.5 px-4">Asset</th><th className="px-3">Type</th><th className="px-3 text-right">Days used</th><th className="px-3 text-right">Hours</th><th className="px-3 text-right">Cost</th><th className="px-3" />
        </tr></thead>
        <tbody>
          {machines.map((m, i) => (
            <tr key={i} onClick={() => setSelected(m)} className="border-b border-zinc-100 dark:border-white/5 hover:bg-zinc-50 dark:hover:bg-white/5" data-testid={`jbd-machine-row-${i}`}>
              <td className="py-2.5 px-4 font-bold">{m.label}</td>
              <td className="px-3 capitalize text-zinc-500">{m.type || (m.kind === "gear" ? "gear" : "—")}</td>
              <td className="px-3 text-right tabular-nums">{m.days}</td>
              <td className="px-3 text-right tabular-nums font-mono">{m.hours}</td>
              <td className="px-3 text-right tabular-nums font-mono font-bold">{fmtMoney(m.cost)}</td>
              <td className="px-3 text-right"><button onClick={(event) => { event.stopPropagation(); setSelected(m); }} className="text-[#FF5F15] hover:underline text-[11px] font-bold" data-testid={`jbd-view-asset-${i}`}>View details</button></td>
            </tr>
          ))}
          <tr className="font-bold border-t-2 border-zinc-200 dark:border-white/10">
            <td className="py-2.5 px-4">Total</td><td className="px-3" /><td className="px-3 text-right tabular-nums">{machines.reduce((a, m) => a + (m.days || 0), 0)}</td>
            <td className="px-3 text-right tabular-nums font-mono">{Number(machines.reduce((a, m) => a + (m.hours || 0), 0).toFixed(2))}</td>
            <td className="px-3 text-right tabular-nums font-mono">{fmtMoney(machines.reduce((a, m) => a + (m.cost || 0), 0))}</td><td />
          </tr>
        </tbody>
      </table>
    </div>
    <Sheet open={!!selected} onOpenChange={open => { if (!open) setSelected(null); }}>
      <SheetContent side="right" className="sm:max-w-2xl">
        {selected && <MachineUsageDetail machine={selected} job={job} period={period} onFleet={() => navigate(`/app/fleet?asset=${encodeURIComponent(selected.asset_id)}`)} />}
      </SheetContent>
    </Sheet>
    </>
  );
}

function MachineUsageDetail({ machine, job, period, onFleet }) {
  const [recordId, setRecordId] = useState(null);
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let current = true;
    setRecord(null);
    setError("");
    if (recordId) api.get(`/timesheet-records/${encodeURIComponent(recordId)}`)
      .then(({ data }) => { if (current) setRecord(data); })
      .catch(() => { if (current) setError("Could not load this timesheet. Close and reopen it to retry."); });
    return () => { current = false; };
  }, [recordId]);
  return <>
    <SheetHeader>
      <SheetTitle>{machine.label}</SheetTitle>
      <SheetDescription>{machine.type || machine.kind} · {job.job_number} · {job.title}<br />{period}</SheetDescription>
    </SheetHeader>
    {recordId ? <div className="mt-5 space-y-4">
      <Button variant="outline" onClick={() => setRecordId(null)}>Back to usage</Button>
      <h3 className="font-semibold">Timesheet · {record?.user_name || ""} {record?.date || ""}</h3>
      {error ? <p role="alert">{error}</p> : record ? <TimesheetInspectionPanel ts={record} onBillingChange={() => api.get(`/timesheet-records/${encodeURIComponent(recordId)}`).then(({ data }) => setRecord(data)).catch(() => setError("Could not refresh this timesheet."))} /> : <p role="status">Loading timesheet…</p>}
    </div> : <>
      <div className="grid grid-cols-3 gap-3 my-6">
        <Kpi label="Days used" value={machine.days} />
        <Kpi label="Total hours" value={fmtHrs(machine.hours)} />
        <Kpi label="Total cost" value={fmtMoney(machine.cost)} />
      </div>
      <h3 className="font-semibold mb-3">Usage breakdown</h3>
      <div className="space-y-3">
        {[...(machine.usage || [])].sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((row, index) => <div key={`${row.timesheet_id}-${index}`} className="rounded-lg border p-3">
          <div className="flex flex-wrap justify-between gap-2"><strong>{row.date ? fmtShort(row.date) : "—"}</strong><span>{fmtHrs(row.hours)} · {fmtMoney(row.cost)}</span></div>
          <p className="text-sm mt-1">{row.worker || "—"} · {row.source}</p>
          <p className="text-xs text-muted-foreground">{row.hours_basis}</p>
          {row.timesheet_id && <Button variant="link" className="px-0" onClick={() => setRecordId(row.timesheet_id)}>View timesheet</Button>}
        </div>)}
        {!machine.usage?.length && <p>Source records unavailable for this summary.</p>}
      </div>
      <div className="flex justify-between border-t mt-4 pt-3 font-semibold"><span>Total</span><span>{fmtHrs(machine.hours)} · {fmtMoney(machine.cost)}</span></div>
      {machine.asset_id && <Button variant="link" className="px-0 mt-4" onClick={onFleet}>View asset in Fleet →</Button>}
    </>}
  </>;
}

// ---------------- SUPPLIES ----------------
function SuppliesTab({ supplies, summary }) {
  if (!supplies || supplies.length === 0) return <Empty>No supplies or materials charged this month.</Empty>;
  return (
    <div>
    <div className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] overflow-x-auto" data-testid="jbd-supplies-table">
      <table className="w-full text-sm min-w-[720px]">
        <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10">
          <th className="py-2.5 px-4">Item</th><th className="px-3">Reference</th><th className="px-3 text-right">Qty</th><th className="px-3">Unit</th><th className="px-3 text-right">Unit cost</th><th className="px-3 text-right">Total</th>
        </tr></thead>
        <tbody>
          {supplies.map((sp, i) => (
            <tr key={i} className="border-b border-zinc-100 dark:border-white/5 hover:bg-zinc-50 dark:hover:bg-white/5" data-testid={`jbd-supply-row-${i}`}>
              <td className="py-2.5 px-4"><strong>{sp.item}</strong>{sp.kind === "supplier_invoice" && <>
                <span className="ml-2 text-[9px] font-bold uppercase text-zinc-400">supplier bill</span>
                <details className="mt-1 min-w-[260px] max-w-lg"><summary className="text-xs text-[#FF5F15] cursor-pointer">Items &amp; source evidence</summary><div className="space-y-2 mt-2"><OriginalInvoice path={sp.file_path} /><SupplierItems items={sp.line_items} currency={sp.currency} matches={sp.fleet_matches} reviewed={sp.operational_reviewed} /></div></details>
              </>}</td>
              <td className="px-3 text-zinc-500 font-mono text-xs">{sp.reference || "—"}</td>
              <td className="px-3 text-right tabular-nums font-mono">{sp.qty != null ? sp.qty : "—"}</td>
              <td className="px-3 text-zinc-500">{sp.unit || "-"}</td>
              <td className="px-3 text-right tabular-nums font-mono">{sp.unit_cost != null ? fmtMoney(sp.unit_cost) : "—"}</td>
              <td className="px-3 text-right tabular-nums font-mono font-bold">{fmtMoney(sp.cost)}</td>
            </tr>
          ))}
          <tr className="font-bold border-t-2 border-zinc-200 dark:border-white/10">
            <td className="py-2.5 px-4" colSpan={5}>Total</td>
            <td className="px-3 text-right tabular-nums font-mono">{fmtMoney(supplies.reduce((a, sp) => a + (sp.cost || 0), 0))}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <SupplierMaterialSummary rows={summary} />
    </div>
  );
}

// ---------------- BILLING ----------------
function BillingTab({ data, onCategory, onInvoiceNow, onMarkInvoiced, marking, jobId }) {
  const navigate = useNavigate();
  const b = data.billing || {};
  const groups = [
    { key: "Labour", value: b.labour_cost || 0, color: CAT.labour },
    { key: "Machines", value: b.machine_cost || 0, color: CAT.machines },
    { key: "Supplies", value: b.supplies_cost || 0, color: CAT.supplies },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] p-4"><div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Total cost</div><div className="text-2xl font-extrabold tabular-nums mt-1">{fmtMoney(b.cost_this_month)}</div></div>
        <div className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] p-4"><div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Invoiced</div><div className="text-2xl font-extrabold tabular-nums mt-1">{fmtMoney(b.invoiced)}</div></div>
        <div className={`rounded-xl border p-4 ${(b.not_invoiced || 0) > 0.005 ? "border-amber-400/60 bg-amber-50 dark:bg-amber-900/10" : "border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620]"}`}><div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Cost less invoiced / external</div><div className={`text-2xl font-extrabold tabular-nums mt-1 ${(b.not_invoiced || 0) > 0.005 ? "text-amber-600" : "text-emerald-600"}`}>{fmtMoney(b.not_invoiced)}</div></div>
      </div>

      <SupplierMaterialSummary rows={data.supplier_material_summary} billing />
      <Card title="Cost by category" icon={Receipt} testid="jbd-billing-groups">
        <div className="space-y-2.5">
          {groups.map((g) => (
            <div key={g.key} className="flex items-center gap-3 text-sm">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: g.color }} />
              <button className="flex-1 text-left font-medium underline" onClick={() => onCategory({Labour: "labour", Machines: "machines", Supplies: "supplies"}[g.key])}>{g.key}</button>
              <span className="tabular-nums font-mono font-bold">{fmtMoney(g.value)}</span>
            </div>
          ))}
          <div className="flex items-center gap-3 text-sm border-t border-zinc-200 dark:border-white/10 pt-2.5 font-bold">
            <span className="flex-1">Total cost</span><span className="tabular-nums font-mono">{fmtMoney(b.cost_this_month)}</span>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mt-3">The cost gap compares internal cost against invoiced and externally marked amounts. It is not a client charge or ready-to-invoice balance. Use Billing readiness and Invoice Now to review sell-rate sources and exclusions.</p>
      </Card>

      <Card title="Customer invoices this month" icon={FileText} testid="jbd-billing-invoices">
        {(!b.invoices || b.invoices.length === 0) ? (
          <div className="text-sm text-zinc-400 italic py-1">No customer invoices issued for this job this month.</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-zinc-200 dark:border-white/10"><th className="py-1.5">Invoice</th><th>Date</th><th>Status</th><th className="text-right">Amount</th><th /></tr></thead>
            <tbody>
              {b.invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-zinc-50 dark:border-white/5 last:border-0">
                  <td className="py-2 font-bold font-mono">{inv.invoice_number}</td>
                  <td className="text-zinc-500">{fmtShort(inv.issue_date)}</td>
                  <td className="capitalize text-zinc-500">{inv.status}</td>
                  <td className="text-right tabular-nums font-mono font-bold">{fmtMoney(inv.total || inv.subtotal)}</td>
                  <td className="text-right"><button onClick={() => navigate(`/app/customer-invoices/${inv.id}`)} className="text-[#FF5F15] hover:underline text-[11px] font-bold inline-flex items-center gap-1" data-testid={`jbd-view-invoice-${inv.invoice_number}`}>View <ExternalLink className="w-3 h-3" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button onClick={onInvoiceNow} className="h-10 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="jbd-billing-invoice-now"><Receipt className="w-4 h-4 mr-1.5" /> Invoice Now</Button>
        <Button variant="outline" onClick={onMarkInvoiced} disabled={marking === jobId} className="h-10 rounded-lg uppercase tracking-wider font-bold text-xs" data-testid="jbd-billing-mark-invoiced">
          {marking === jobId ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Mark Invoiced
        </Button>
      </div>
    </div>
  );
}

const Empty = ({ children }) => (
  <div className="rounded-xl border-2 border-dashed border-zinc-300 dark:border-white/10 p-10 text-center text-sm text-zinc-500">{children}</div>
);
